import React, { useEffect, useState, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon } from 'lucide-react';
// Fix for Leaflet marker icon issues
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});
// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Type for project location
export type ProjectLocation = {
  id: number;
  title: string;
  description: string;
  lat: number;
  lng: number;
  type: string;
  image?: string;
};
type ProjectLocationMapProps = {
  locations: ProjectLocation[];
  height?: string;
  centerLat?: number;
  centerLng?: number;
  zoom?: number;
  onMarkerClick?: (project: ProjectLocation) => void;
};
// Component to update map view when center changes
const ChangeView = ({
  center,
  zoom
}: {
  center: [number, number];
  zoom: number;
}) => {
  const map = useMap();
  map.setView(center, zoom);
  return null;
};
export const ProjectLocationMap: React.FC<ProjectLocationMapProps> = ({
  locations,
  height = '400px',
  // Default center is now Malaysia
  centerLat = 4.2105,
  centerLng = 101.9758,
  zoom = 6,
  onMarkerClick
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>([centerLat, centerLng]);
  const [mapZoom, setMapZoom] = useState<number>(zoom);
  // Update map center if props change
  useEffect(() => {
    setMapCenter([centerLat, centerLng]);
    setMapZoom(zoom);
  }, [centerLat, centerLng, zoom]);
  const handleMarkerClick = (project: ProjectLocation) => {
    setMapCenter([project.lat, project.lng]);
    setMapZoom(10);
    if (onMarkerClick) {
      onMarkerClick(project);
    }
  };
  // Get color based on project type
  const getMarkerColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'paddy':
        return 'text-leaf-green';
      case 'palm':
        return 'text-barn-red';
      case 'technology':
        return 'text-sky-blue';
      case 'organic':
        return 'text-leaf-green';
      case 'aquaculture':
        return 'text-sky-blue';
      case 'durian':
        return 'text-wheat-gold';
      default:
        return 'text-wheat-gold';
    }
  };
  return <div style={{
    height,
    width: '100%'
  }} className="rounded-lg overflow-hidden border border-gray-200" data-id="element-446">
      <MapContainer center={mapCenter} zoom={mapZoom} style={{
      height: '100%',
      width: '100%'
    }} scrollWheelZoom={false} data-id="element-447">
        <ChangeView center={mapCenter} zoom={mapZoom} data-id="element-448" />
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-449" />
        {locations.map(location => <Marker key={location.id} position={[location.lat, location.lng]} icon={customIcon} eventHandlers={{
        click: () => handleMarkerClick(location)
      }} data-id="element-450">
            <Popup data-id="element-451">
              <div className="w-48" data-id="element-452">
                {location.image && <img src={location.image} alt={location.title} className="w-full h-24 object-cover mb-2 rounded" data-id="element-453" />}
                <h3 className="font-medium text-soil-brown" data-id="element-454">
                  {location.title}
                </h3>
                <p className="text-xs text-gray-600 mt-1" data-id="element-455">
                  {location.description}
                </p>
                <div className="flex items-center mt-2" data-id="element-456">
                  <MapPinIcon size={14} className={getMarkerColor(location.type)} data-id="element-457" />
                  <span className="text-xs ml-1" data-id="element-458">{location.type}</span>
                </div>
              </div>
            </Popup>
          </Marker>)}
      </MapContainer>
    </div>;
};